import { useState, forwardRef, useRef, memo } from 'react';
import { useSelector } from 'react-redux';
import {
    getYear,
    getMonth,
    startOfDay,
    subDays,
    subWeeks,
    subMonths,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    startOfYear,
    isSameDay,
} from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { styled } from '@mui/system';
import useMediaQuery from '@mui/material/useMediaQuery';
import MenuList from '@mui/material/MenuList';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { I18N_DATE_FORMAT, getMonthsList, getWeekStartsOn } from '@helpers/dates';
import { CssMenuItem } from '@components/Select';
import { Input as BaseInput } from '@components/Input';
import { useTranslation } from '@hooks';
import './styles.css';

const getLastWeekRange = weekStartsOn => {
    const lastWeek = subWeeks(new Date(), 1);
    return [startOfWeek(lastWeek, { weekStartsOn }), endOfWeek(lastWeek, { weekStartsOn })];
};

const getLastMonthRange = () => {
    const lastMonth = subMonths(new Date(), 1);
    return [startOfMonth(lastMonth), endOfMonth(lastMonth)];
};

const TIMEFRAME_PRESETS = [
    { key: 'today', getRange: () => [startOfDay(new Date()), new Date()] },
    { key: 'last7Days', getRange: () => [startOfDay(subDays(new Date(), 6)), new Date()] },
    { key: 'lastWeek', getRange: getLastWeekRange },
    { key: 'last30Days', getRange: () => [startOfDay(subDays(new Date(), 29)), new Date()] },
    { key: 'last90Days', getRange: () => [startOfDay(subDays(new Date(), 89)), new Date()] },
    { key: 'thisMonth', getRange: () => [startOfMonth(new Date()), new Date()] },
    { key: 'lastMonth', getRange: getLastMonthRange },
    { key: 'thisYear', getRange: () => [startOfYear(new Date()), new Date()] },
    { key: 'allTime', getRange: () => [null, null] },
];

const isActivePreset = (value, [start, end]) =>
    start === null
        ? !value[0] && !value[1]
        : !!value[0] && !!value[1] && isSameDay(value[0], start) && isSameDay(value[1], end);

const revealPreset = item => {
    const list = item?.offsetParent;
    if (!list) return;
    const visibleHeight = list.clientHeight - parseFloat(getComputedStyle(list).paddingBottom);
    const minScrollTop = item.offsetTop + item.offsetHeight - visibleHeight;
    list.scrollTop = Math.min(Math.max(list.scrollTop, minScrollTop), item.offsetTop);
};

const MultiselectErrorWrapper = styled('div')(({ error }) => ({
    position: 'relative',
    height: 'fit-content',
    '& .MuiInputLabel-root': {
        color: error && 'var(--error-main)',
    },
    '& .MuiOutlinedInput-root fieldset, .MuiOutlinedInput-root:hover fieldset': {
        borderColor: error && 'var(--error-main)',
    },
    '& .MuiOutlinedInput-root.Mui-focused fieldset': {
        borderColor: error && 'var(--error-main)',
    },
}));

const ErrText = styled('p')({
    position: 'absolute',
    bottom: '-16px',
    left: 11,
    color: 'var(--error-main)',
    fontFamily: 'Rubik,sans-serif',
    fontSize: '12px',
    lineHeight: '14px',
    letterSpacing: '0.4px',
});

const Presets = styled(MenuList)({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    minWidth: 156,
    maxHeight: 224,
    paddingBottom: 12,
    overflowY: 'auto',
    '& > *': {
        flexShrink: 0,
    },
    '@media (max-width: 600px)': {
        '.timeframe-range-calendar &': {
            width: '100%',
            minWidth: 0,
            maxHeight: '40vh',
            marginBottom: 8,
            paddingBottom: 8,
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        },
    },
});

const MonthYearSelect = styled('select')({
    outline: 'none',
    border: 'none',
    fontFamily: 'Rubik, sans-serif',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '0.875rem', //14px
    lineHeight: '16px',
    letterSpacing: '0.1px',
});

const CustomInput = forwardRef(
    (
        {
            label,
            showTimeSelect,
            isRequired,
            onBlurTimeSelect,
            value,
            onClick,
            isDisabled,
            isOpen,
            range,
            dateRef,
            onClear,
        },
        ref,
    ) => {
        const { t } = useTranslation();
        const clears = !!onClear && !!value && !isDisabled;
        const iconSx = { fill: isOpen ? 'var(--primary-main)' : 'var(--text-tertiary)', fontSize: 16 };
        return (
            <BaseInput
                label={label}
                addonComponent={clears ? <CloseRoundedIcon sx={iconSx} /> : <CalendarTodayRoundedIcon sx={iconSx} />}
                onAddonClick={() => {
                    if (isDisabled) return;
                    if (clears) onClear();
                    else dateRef.current.setOpen(!isOpen);
                }}
                fullWidth
                inputRef={ref}
                disabled={isDisabled}
                required={isRequired}
                inputProps={{
                    value: value ? value : showTimeSelect || !range ? '' : t('common.allTime'),
                    readOnly: true,
                    onClick,
                    onBlur: isOpen ? null : onBlurTimeSelect,
                }}
            />
        );
    },
);

CustomInput.displayName = 'CustomInput';

const TimeFramePicker = memo(
    ({
        className,
        label = '',
        onChangeTimeframe,
        disabled,
        required = false,
        range = true,
        value = [new Date(), null],
        showTimeSelect = false,
        onBlurTimeSelect = null,
        dateFormat = 'd MMM yyyy',
        minDate = new Date(),
        maxDate = null,
        clearable = false,
        error,
    }) => {
        const { t } = useTranslation();
        const [isCalendarOpen, setCalendarOpen] = useState(false);
        const language = useSelector(state => state.auth.user?.language);
        const accountLocale = useSelector(state => state.account?.account?.locale);
        const weekStartsOn = getWeekStartsOn(accountLocale);
        const isMobile = useMediaQuery('(max-width:600px)');
        const dateRef = useRef();

        const onChange = dates => onChangeTimeframe(dates);

        const applyRange = nextRange => {
            if (!isActivePreset(value, nextRange)) onChangeTimeframe(nextRange);
            setCalendarOpen(false);
            dateRef.current.setOpen(false);
        };

        const filterPassedTime = time => {
            if (maxDate && !range) {
                const selectedDate = new Date(time);
                return selectedDate.getTime() < maxDate.getTime();
            }
            const currentDate = new Date();
            const selectedDate = new Date(time);

            return currentDate.getTime() < selectedDate.getTime();
        };

        const minMaxProps = range ? { minDate: null, maxDate: new Date() } : { minDate, maxDate };
        const minYear = minDate ? getYear(minDate) : getYear(new Date()) - 50;
        const maxYear = maxDate ? getYear(maxDate) : getYear(new Date()) + 20;
        const yearsRange = (start, stop, step) =>
            Array.from({ length: (stop - start) / step + 1 }, (_, i) => start + i * step);
        const years = yearsRange(maxYear, minYear, -1);
        const months = getMonthsList(I18N_DATE_FORMAT[language]);

        return (
            <MultiselectErrorWrapper className={className} error={!!error}>
                <DatePicker
                    ref={dateRef}
                    withPortal={range && isMobile}
                    calendarClassName={range ? 'timeframe-range-calendar' : undefined}
                    popperPlacement="bottom-start"
                    selected={value[0]}
                    onChange={onChange}
                    startDate={value[0]}
                    endDate={value[1]}
                    selectsRange={range}
                    showTimeSelect={showTimeSelect}
                    timeFormat="HH:mm"
                    calendarStartDay={weekStartsOn}
                    locale={I18N_DATE_FORMAT[language]}
                    dateFormat={dateFormat}
                    onCalendarClose={() => setCalendarOpen(false)}
                    onCalendarOpen={() => setCalendarOpen(true)}
                    maxDate={range ? new Date() : null}
                    minDate={range ? null : new Date()}
                    filterTime={range ? null : filterPassedTime}
                    {...minMaxProps}
                    customInput={
                        <CustomInput
                            dateRef={dateRef}
                            label={label}
                            showTimeSelect={showTimeSelect}
                            isOpen={isCalendarOpen}
                            isDisabled={disabled}
                            isRequired={required}
                            onBlurTimeSelect={onBlurTimeSelect}
                            range={range}
                            onClear={clearable && !range ? () => onChangeTimeframe(null) : undefined}
                        />
                    }
                    monthsShown={1}
                    renderCustomHeader={({
                        date,
                        decreaseMonth,
                        increaseMonth,
                        changeYear,
                        changeMonth,
                        prevMonthButtonDisabled,
                        nextMonthButtonDisabled,
                    }) => (
                        <div className="navigation-container">
                            <button
                                aria-label="Previous Month"
                                className="react-datepicker__navigation react-datepicker__navigation--previous"
                                style={{ marginLeft: '8px' }}
                                onClick={decreaseMonth}
                                type="button"
                                disabled={prevMonthButtonDisabled}
                            >
                                <ArrowBackRoundedIcon sx={{ fill: '#123341' }} />
                            </button>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <MonthYearSelect
                                    value={getYear(date)}
                                    onChange={({ target: { value } }) => changeYear(value)}
                                >
                                    {years.map(option => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </MonthYearSelect>
                                <MonthYearSelect
                                    value={months[getMonth(date)]}
                                    onChange={({ target: { value } }) => changeMonth(months.indexOf(value))}
                                >
                                    {months.map(option => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </MonthYearSelect>
                            </div>
                            <button
                                aria-label="Next Month"
                                className={'react-datepicker__navigation react-datepicker__navigation--next'}
                                style={{ marginRight: '8px' }}
                                onClick={increaseMonth}
                                type="button"
                                disabled={nextMonthButtonDisabled}
                            >
                                <ArrowForwardRoundedIcon sx={{ fill: '#123341' }} />
                            </button>
                        </div>
                    )}
                >
                    {range ? (
                        <Presets disablePadding>
                            {TIMEFRAME_PRESETS.map(({ key, getRange }) => {
                                const presetRange = getRange(weekStartsOn);
                                const isActive = isActivePreset(value, presetRange);
                                return (
                                    <CssMenuItem
                                        key={key}
                                        ref={isActive ? revealPreset : undefined}
                                        selected={isActive}
                                        onClick={() => applyRange(presetRange)}
                                    >
                                        {t(`common.${key}`)}
                                    </CssMenuItem>
                                );
                            })}
                        </Presets>
                    ) : null}
                </DatePicker>
                {error ? <ErrText>{error ? t(error.message) : null}</ErrText> : null}
            </MultiselectErrorWrapper>
        );
    },
);

TimeFramePicker.displayName = 'TimeFramePicker';

export default TimeFramePicker;
